import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  ProfileCompleteness,
  SellerActivityPayload,
  SellerBrokerInterestPayload,
  SellerDashboardStats,
  SellerDocumentActionItem,
  SellerEnquiryRow,
  SellerLawyerReviewRow,
  SellerNotificationRow,
  SellerPropertyOverviewRow
} from '@/types/sellerDashboard';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import { propertyApi } from '../Property/PropertyService';
import type {
  ReuploadDashboardDocumentArg,
  SellerActivityArg,
  SellerBuyerEnquiriesArg,
  SellerBuyerEnquiryDetail,
  SellerBuyerEnquiryListResult,
  SellerDashboardDocument,
  SellerEnquiriesArg,
  SellerEnquiryDetail,
  SellerEnquiryListResult,
  SellerNotificationIdArg,
  SellerPagination,
  UploadDashboardDocumentArg
} from './sellerTypes';

/**
 * The seller's own surfaces: `/api/seller/dashboard/*`, `/api/user/notifications/:id/read`,
 * `/api/seller/profile/completeness` and `/api/seller/enquiries*`. All under `/api`, so
 * `apiBaseUrl`.
 *
 * Normalizers are module-scope, take `unknown` and are TOTAL — a list answers `[]`, an
 * object answers a filled default. A throwing normalizer takes out the route's error
 * boundary instead of letting the query surface its own error state.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const envelope = (response: unknown): unknown => {
  const root = asRecord(response);
  return 'data' in root ? root.data : response;
};

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return fallback;
};

/**
 * Axios dropped `undefined` params; `URLSearchParams` would stringify them as "undefined",
 * which the backend reads as a real filter. Everything nullish is skipped so the query
 * string is byte-identical to what the axios services sent.
 */
const toQuery = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export function normalizeDashboardStats(response: unknown): SellerDashboardStats {
  const data = asRecord(envelope(response));
  return {
    totalProperties: asNumber(data.totalProperties),
    documentsNeedingAction: asNumber(data.documentsNeedingAction),
    underLawyerReview: asNumber(data.underLawyerReview),
    verifiedProperties: asNumber(data.verifiedProperties),
    brokerEnquiries: asNumber(data.brokerEnquiries),
    draftProperties: asNumber(data.draftProperties)
  };
}

export function normalizeDocumentActions(response: unknown): SellerDocumentActionItem[] {
  const data = envelope(response);
  if (Array.isArray(data)) return data as SellerDocumentActionItem[];
  return asArray<SellerDocumentActionItem>(asRecord(data).items);
}

export function normalizePropertiesOverview(response: unknown): SellerPropertyOverviewRow[] {
  const data = envelope(response);
  if (Array.isArray(data)) return data as SellerPropertyOverviewRow[];
  return asArray<SellerPropertyOverviewRow>(asRecord(data).items);
}

export function normalizeLawyerReviews(response: unknown): SellerLawyerReviewRow[] {
  const data = envelope(response);
  if (Array.isArray(data)) return data as SellerLawyerReviewRow[];
  return asArray<SellerLawyerReviewRow>(asRecord(data).items);
}

export function normalizeBrokerInterest(response: unknown): SellerBrokerInterestPayload {
  const data = asRecord(envelope(response));
  const summary = asRecord(data.summary);
  return {
    summary: {
      totalReceived: asNumber(summary.totalReceived),
      adminApproved: asNumber(summary.adminApproved),
      pendingAdminReview: asNumber(summary.pendingAdminReview)
    },
    perProperty: asArray<SellerBrokerInterestPayload['perProperty'][number]>(data.perProperty)
  };
}

/**
 * Pagination arrives as `{ pagination }` here but as flat `{ total, page, limit }` on the
 * lawyer and public endpoints — both shapes are absorbed so no component branches on it.
 */
function normalizePagination(value: unknown, itemCount: number): SellerPagination {
  const raw = asRecord(value);
  const nested = asRecord(raw.pagination);
  const source = Object.keys(nested).length > 0 ? nested : raw;
  const limit = asNumber(source.limit, itemCount || 10);
  const total = asNumber(source.total, itemCount);
  return {
    page: asNumber(source.page, 1),
    limit,
    total,
    totalPages: asNumber(source.totalPages, limit > 0 ? Math.ceil(total / limit) : 1)
  };
}

export function normalizeSellerActivity(response: unknown): SellerActivityPayload {
  const data = envelope(response);
  const items = Array.isArray(data)
    ? (data as SellerActivityPayload['items'])
    : asArray<SellerActivityPayload['items'][number]>(asRecord(data).items);
  return { items, pagination: normalizePagination(data, items.length) };
}

export function normalizeNotifications(response: unknown): SellerNotificationRow[] {
  const data = envelope(response);
  if (Array.isArray(data)) return data as SellerNotificationRow[];
  return asArray<SellerNotificationRow>(asRecord(data).items);
}

export function normalizeProfileCompleteness(response: unknown): ProfileCompleteness {
  const data = asRecord(envelope(response));
  return {
    percentage: asNumber(data.percentage),
    missing: asArray<unknown>(data.missing).map((entry) => asString(entry))
  };
}

export function normalizeDashboardDocument(response: unknown): SellerDashboardDocument {
  const data = asRecord(envelope(response));
  const base: SellerDashboardDocument = {
    id: asNumber(data.id),
    propertyId: asNumber(data.propertyId),
    fileName: asString(data.fileName),
    fileUrl: asString(data.fileUrl),
    fileSize: asNumber(data.fileSize),
    documentType: asString(data.documentType, 'other')
  };
  return data.version === undefined || data.version === null
    ? base
    : { ...base, version: asNumber(data.version) };
}

export function normalizeEnquiryList(response: unknown): SellerEnquiryListResult {
  const data = envelope(response);
  const items = Array.isArray(data)
    ? (data as SellerEnquiryRow[])
    : asArray<SellerEnquiryRow>(asRecord(data).items);
  return { items, pagination: normalizePagination(data, items.length) };
}

export function normalizeEnquiryDetail(response: unknown): SellerEnquiryDetail | null {
  const data = asRecord(envelope(response));
  return Object.keys(data).length > 0 ? (data as SellerEnquiryDetail) : null;
}

export function normalizeBuyerEnquiryList(response: unknown): SellerBuyerEnquiryListResult {
  const data = envelope(response);
  const items = Array.isArray(data)
    ? (data as SellerBuyerEnquiryListResult['items'])
    : asArray<SellerBuyerEnquiryListResult['items'][number]>(asRecord(data).items);
  return { items, pagination: normalizePagination(data, items.length) };
}

export function normalizeBuyerEnquiryDetail(response: unknown): SellerBuyerEnquiryDetail | null {
  const data = asRecord(envelope(response));
  return Object.keys(data).length > 0 ? (data as SellerBuyerEnquiryDetail) : null;
}

export const sellerApi = createApi({
  reducerPath: 'sellerApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: [
    'DashboardStats',
    'PropertyDocument',
    'Property',
    'PropertyActivity',
    'Notification',
    'Profile',
    'Enquiry',
    'BuyerEnquiry'
  ],
  endpoints: (builder) => ({
    getSellerDashboardStats: builder.query<SellerDashboardStats, void>({
      query: () => ({ url: 'seller/dashboard/stats' }),
      transformResponse: (response: unknown) => normalizeDashboardStats(response),
      providesTags: ['DashboardStats']
    }),
    getSellerDocumentActions: builder.query<SellerDocumentActionItem[], void>({
      query: () => ({ url: 'seller/dashboard/document-actions' }),
      transformResponse: (response: unknown) => normalizeDocumentActions(response),
      providesTags: [{ type: 'PropertyDocument', id: 'LIST' }]
    }),
    getSellerPropertiesOverview: builder.query<SellerPropertyOverviewRow[], void>({
      query: () => ({ url: 'seller/dashboard/properties-overview' }),
      transformResponse: (response: unknown) => normalizePropertiesOverview(response),
      providesTags: [{ type: 'Property', id: 'LIST' }]
    }),
    getSellerLawyerReviews: builder.query<SellerLawyerReviewRow[], void>({
      query: () => ({ url: 'seller/dashboard/lawyer-reviews' }),
      transformResponse: (response: unknown) => normalizeLawyerReviews(response),
      providesTags: [{ type: 'Property', id: 'LAWYER_REVIEWS' }]
    }),
    getSellerBrokerInterest: builder.query<SellerBrokerInterestPayload, void>({
      query: () => ({ url: 'seller/dashboard/broker-interest' }),
      transformResponse: (response: unknown) => normalizeBrokerInterest(response),
      providesTags: [{ type: 'Enquiry', id: 'BROKER_INTEREST' }]
    }),
    /** `page`/`limit` default to 1/10 — the axios signature defaulted them, so they were
     * always on the wire and the cache key must stay the same. */
    getSellerDashboardActivity: builder.query<SellerActivityPayload, SellerActivityArg | void>({
      query: (arg) => ({
        url: `seller/dashboard/activity${toQuery({
          page: arg?.page ?? 1,
          limit: arg?.limit ?? 10
        })}`
      }),
      transformResponse: (response: unknown) => normalizeSellerActivity(response),
      providesTags: [{ type: 'PropertyActivity', id: 'LIST' }]
    }),
    getSellerDashboardNotifications: builder.query<SellerNotificationRow[], void>({
      query: () => ({ url: 'seller/dashboard/notifications' }),
      transformResponse: (response: unknown) => normalizeNotifications(response),
      providesTags: [{ type: 'Notification', id: 'DASHBOARD' }]
    }),
    /** Still used by the dashboard's `NotificationsInbox` widget to mark a row read. */
    markSellerNotificationRead: builder.mutation<void, SellerNotificationIdArg>({
      query: ({ id }) => ({ url: `user/notifications/${id}/read`, method: 'PUT' }),
      invalidatesTags: [{ type: 'Notification', id: 'DASHBOARD' }]
    }),
    getSellerProfileCompleteness: builder.query<ProfileCompleteness, void>({
      query: () => ({ url: 'seller/profile/completeness' }),
      transformResponse: (response: unknown) => normalizeProfileCompleteness(response),
      providesTags: ['Profile']
    }),
    /**
     * FormData body with NO `Content-Type`: the browser must set the multipart boundary
     * itself or multer parses zero fields.
     */
    reuploadSellerDashboardDocument: builder.mutation<
      SellerDashboardDocument,
      ReuploadDashboardDocumentArg
    >({
      query: ({ docId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `seller/dashboard/document-actions/${docId}/reupload`,
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response: unknown) => normalizeDashboardDocument(response),
      invalidatesTags: [
        { type: 'PropertyDocument', id: 'LIST' },
        'DashboardStats',
        { type: 'Property', id: 'LIST' }
      ],
      /** The property's own document list lives in `propertyApi`; a tag of the same name in
       * this slice would not reach it, so the cross-slice hit goes through `onQueryStarted`. */
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const propertyId = arg.propertyId ?? data.propertyId;
          if (!propertyId) return;
          dispatch(
            propertyApi.util.invalidateTags([
              { type: 'PropertyDocument', id: String(propertyId) },
              { type: 'Property', id: String(propertyId) }
            ])
          );
        } catch {
          /* a failed upload changed nothing — the mutation's own error state covers it */
        }
      }
    }),
    uploadSellerDashboardDocument: builder.mutation<
      SellerDashboardDocument,
      UploadDashboardDocumentArg
    >({
      query: ({ propertyId, documentType, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('propertyId', String(propertyId));
        formData.append('documentType', documentType);
        return {
          url: 'seller/dashboard/document-actions/upload',
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response: unknown) => normalizeDashboardDocument(response),
      invalidatesTags: [
        { type: 'PropertyDocument', id: 'LIST' },
        'DashboardStats',
        { type: 'Property', id: 'LIST' }
      ],
      async onQueryStarted({ propertyId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            propertyApi.util.invalidateTags([
              { type: 'PropertyDocument', id: String(propertyId) },
              { type: 'Property', id: String(propertyId) }
            ])
          );
        } catch {
          /* a failed upload changed nothing */
        }
      }
    }),

    /* `/api/seller/enquiries` — `{ items, pagination }`, unlike the lawyer/public lists. */
    getSellerEnquiries: builder.query<SellerEnquiryListResult, SellerEnquiriesArg>({
      query: ({ page, limit, propertyId, status }) => ({
        url: `seller/enquiries${toQuery({ page, limit, propertyId, status })}`
      }),
      transformResponse: (response: unknown) => normalizeEnquiryList(response),
      providesTags: (result) => [
        { type: 'Enquiry' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({
          type: 'Enquiry' as const,
          id: String(row.id)
        }))
      ]
    }),
    getSellerEnquiryDetail: builder.query<SellerEnquiryDetail | null, { id: number }>({
      query: ({ id }) => ({ url: `seller/enquiries/${id}` }),
      transformResponse: (response: unknown) => normalizeEnquiryDetail(response),
      providesTags: (_result, _error, { id }) => [{ type: 'Enquiry', id: String(id) }]
    }),

    /* `/api/seller/buyer-enquiries` — every row is already admin-forwarded. */
    getSellerBuyerEnquiries: builder.query<SellerBuyerEnquiryListResult, SellerBuyerEnquiriesArg>({
      query: ({ page, limit, status }) => ({
        url: `seller/buyer-enquiries${toQuery({ page, limit, status })}`
      }),
      transformResponse: (response: unknown) => normalizeBuyerEnquiryList(response),
      providesTags: (result) => [
        { type: 'BuyerEnquiry' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({
          type: 'BuyerEnquiry' as const,
          id: String(row.id)
        }))
      ]
    }),
    getSellerBuyerEnquiryDetail: builder.query<SellerBuyerEnquiryDetail | null, { id: number }>({
      query: ({ id }) => ({ url: `seller/buyer-enquiries/${id}` }),
      transformResponse: (response: unknown) => normalizeBuyerEnquiryDetail(response),
      providesTags: (_result, _error, { id }) => [{ type: 'BuyerEnquiry', id: String(id) }]
    })
  })
});

export const {
  useGetSellerDashboardStatsQuery,
  useGetSellerDocumentActionsQuery,
  useGetSellerPropertiesOverviewQuery,
  useGetSellerLawyerReviewsQuery,
  useGetSellerBrokerInterestQuery,
  useGetSellerDashboardActivityQuery,
  useGetSellerDashboardNotificationsQuery,
  useMarkSellerNotificationReadMutation,
  useGetSellerProfileCompletenessQuery,
  useReuploadSellerDashboardDocumentMutation,
  useUploadSellerDashboardDocumentMutation,
  useGetSellerEnquiriesQuery,
  useGetSellerEnquiryDetailQuery,
  useGetSellerBuyerEnquiriesQuery,
  useGetSellerBuyerEnquiryDetailQuery
} = sellerApi;
